/**
 * invoiceLifecycle.js — Invoice lifecycle, terms, and aging derivation.
 *
 * F1 spec:
 * - Invoice basis = job's uninvoiced balance = accepted_total − Σ(non-void invoices)
 * - Terms sourced from canonical `payment_term.v1` seed (`useSettings.getOptions`).
 * - Due date derived: invoicedDate + behavior.offset_days (see `deriveDueDate`).
 * - Aging derived (NEVER stored): Paid / Unsent / Sent / Due soon (≤3d) / Past due (N days).
 * - 50/50 creates TWO invoice records (Deposit + Completion).
 * - Numbering: global INV-xxxxxx.
 * - Persisted nullable fields for future payments.
 *
 * PT-23 changes (2026-07-26):
 *   • `INVOICE_TERMS` local shadow of `payment_term.v1` → DELETED. Callers now
 *     read the canonical seed via `useSettings.getOptions('payment_terms')`
 *     and pass the option list into `deriveDueDate` (Shape A).
 *   • `deriveDueDate` signature is now `(invoicedDate, termsId, paymentTermsOptions)`
 *     and routes on `behavior.kind` from the seed — not a local table.
 *   • `deriveAging` keys on canonical value `'draft'` (backend `types.go`
 *     `InvoiceStatusDraft`) with defensive `'unsent'` fallback for any stray
 *     records, and returns label `'Unsent'` — same vocabulary the
 *     `invoice_status.v1` seed emits via `getEnumLabel`. One vocabulary at
 *     the surface, `draft` value stability at the wire.
 */

import { parseDate } from '../../utils/formatDate';

// ─── Sent-ness (IDI-337 / P-14 / IDI-193) ────────────────────────────────────

/**
 * The invoice's stored status, normalised: lower-cased, legacy `'unsent'`
 * folded into `'draft'`, missing → `'draft'`. Every invoice surface reads
 * status through this so they cannot disagree on spelling.
 *
 * @param {object|null|undefined} invoice
 * @returns {string}
 */
export function invoiceStatus(invoice) {
  const s = String(invoice?.status ?? '').trim().toLowerCase();
  if (!s || s === 'unsent') return 'draft';
  return s;
}

/**
 * IDI-337: THE sent-ness predicate. Reads the stored `status` only — never
 * `sentAt` (older sent records carry no `sentAt`; the list used to gate on
 * it and read UNSENT while the detail read SENT). Paid implies sent.
 *
 * @param {object|null|undefined} invoice
 * @returns {boolean}
 */
export function isInvoiceSent(invoice) {
  const s = invoiceStatus(invoice);
  return s === 'sent' || s === 'paid';
}

/**
 * IDI-518: may this invoice be marked paid? Sent invoices, including those
 * whose aging reads Past due. Past due is derived (never stored), but a stray
 * `past_due` status is folded in too so the detail page, the row ⋮ and the
 * customer tab's bulk checkboxes cannot disagree.
 *
 * @param {object|null|undefined} invoice
 * @returns {boolean}
 */
export function canMarkInvoicePaid(invoice) {
  const s = invoiceStatus(invoice);
  return s === 'sent' || s === 'past_due';
}

// ─── Line Qty × Rate (IDI-818 D1, ruled 2026-11-03; was IDI-337 09-30) ─────

/** The line total in cents (`line_total ?? lineTotal ?? amount`), or null. */
export function lineTotalCents(item) {
  const raw = item?.line_total ?? item?.lineTotal ?? item?.amount;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function positiveNumber(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * IDI-818 (D1): the ONE Qty × Rate = Amount rule for an invoice line, shared
 * by the invoice page and Review & Send; the PDF's twin is Go's
 * invoiceQtyRate (internal/pdf/invoice.go). Qty: the stored positive
 * `quantity`, else legacy `qty`, else a positive `sqft` (a change order's
 * added line stores only sqft), else 1. Rate: Amount ÷ Qty to the cent, half
 * away from zero; never the stored `unit_rate`. Rate is null (blank) only when
 * it would read $0.00 against a non-zero amount, or there is no amount.
 *
 * @param {object} item
 * @returns {{qty: number, rate: number|null}} rate in cents
 */
export function lineQtyRate(item) {
  const qty = positiveNumber(item?.quantity) ?? positiveNumber(item?.qty) ?? positiveNumber(item?.sqft) ?? 1;
  const total = lineTotalCents(item);
  if (total === null) return { qty, rate: null };
  const rate = Math.sign(total) * Math.round(Math.abs(total) / qty) + 0;
  if (rate === 0 && total !== 0) return { qty, rate: null };
  return { qty, rate };
}

/** The quantity a line reads (IDI-818 D1, see lineQtyRate). */
export function lineQuantity(item) {
  return lineQtyRate(item).qty;
}

/** RATE = line total ÷ Qty, to the cent (IDI-818 D1, see lineQtyRate); null = blank. */
export function deriveLineRate(item) {
  return lineQtyRate(item).rate;
}

// ─── Aging Derivation (NEVER stored — always computed) ───────────────────────

/**
 * Derive invoice aging status. This is NEVER persisted — always computed on read.
 *
 * @param {object} invoice
 * @param {string} invoice.status - 'paid' | 'sent' | 'draft' | 'void' (canonical)
 *   Backwards compatible with legacy `'unsent'` records via defensive fallback.
 * @param {string} invoice.dueDate - ISO date
 * @param {string} invoice.invoicedDate - ISO date
 * @returns {{aging: string, daysOverdue: number|null, tone: string}}
 */
export function deriveAging(invoice) {
  if (!invoice) return { aging: 'unknown', daysOverdue: null, tone: 'neutral' };

  const status = invoiceStatus(invoice);

  // Paid — terminal
  if (status === 'paid') {
    return { aging: 'Paid', daysOverdue: null, tone: 'success' };
  }

  // Void — terminal
  if (status === 'void') {
    return { aging: 'Void', daysOverdue: null, tone: 'neutral' };
  }

  // Not-yet-sent (PT-23 23.5): canonical value is `'draft'` per backend
  // (types.go `InvoiceStatusDraft`) + all live consumers. `'unsent'` is a
  // defensive fallback for any stray legacy record. Label is `'Unsent'` —
  // matches the `invoice_status.v1` seed label so list (via deriveAging)
  // and detail (via getEnumLabel) render the same vocabulary.
  // IDI-337: sent-ness comes from the stored status via isInvoiceSent —
  // the same predicate the detail page uses. No `sentAt` gate.
  if (!isInvoiceSent(invoice)) {
    return { aging: 'Unsent', daysOverdue: null, tone: 'neutral' };
  }

  // Sent — check due date (bare YYYY-MM-DD parsed as LOCAL midnight).
  const due = parseDate(invoice.dueDate ?? invoice.due_date);
  if (!due) {
    return { aging: 'Sent', daysOverdue: null, tone: 'dark' };
  }

  // Compare calendar days (local), so an invoice due today is not past due.
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round((today - dueDay) / (24 * 60 * 60 * 1000));

  if (diffDays > 0) {
    // Past due
    return { aging: 'Past due', daysOverdue: diffDays, tone: 'danger' };
  }

  if (diffDays >= -3) {
    // Due soon (within 3 days)
    return { aging: 'Due soon', daysOverdue: null, tone: 'warning' };
  }

  // Sent, not yet due
  return { aging: 'Sent', daysOverdue: null, tone: 'dark' };
}

// ─── Due Date Derivation ─────────────────────────────────────────────────────

/**
 * Derive due date from invoiced date + canonical `payment_term.v1` seed.
 *
 * **PT-23 Shape A refactor:** caller threads `paymentTermsOptions` (result of
 * `useSettings.getOptions('payment_terms')`) so this helper stays pure and
 * unit-testable with an array-literal fixture — no context coupling, no
 * module-scope shadow constant.
 *
 * Threading the options is chosen for TESTABILITY, not tenant customization:
 * `payment_term.v1` is `custom: false` (closed set). A future refactor that
 * argues "threading buys tenant flexibility" would be wrong — there isn't any.
 *
 * Routes on `behavior.kind` from the seed:
 *   • `'due_date_offset'`       → invoicedDate + `behavior.offset_days`
 *   • `'due_on_acceptance'`     → invoicedDate (same-day, offset 0)
 *   • `'milestone_split'`       → null (PT-26.3 layers on split logic)
 *   • unknown / missing options → null (defensive; caller decides)
 *
 * @param {string} invoicedDate - ISO date
 * @param {string} termsId - Term value from `payment_term.v1` (e.g. 'net_30')
 * @param {Array<{value:string, behavior:{kind:string, offset_days?:number}}>} paymentTermsOptions
 *   Canonical seed options. Callers construct via `getOptions('payment_terms')`.
 * @returns {string|null} ISO date (YYYY-MM-DD) or null if not derivable.
 */
export function deriveDueDate(invoicedDate, termsId, paymentTermsOptions) {
  if (!invoicedDate) return null;
  const opts = Array.isArray(paymentTermsOptions) ? paymentTermsOptions : [];
  // IDI-451: a term is referred to by its id (migrated: "791"), its value
  // (seed: "net_30") or its NAME (stored invoices: "Net 30"). Match any.
  const key = String(termsId ?? '').trim();
  const term = opts.find((o) => o && [o.value, o.id, o.name, o.label].some((v) => v != null && String(v).trim().toLowerCase() === key.toLowerCase()));
  const effectiveBehavior = termBehavior(term, key);
  if (!effectiveBehavior || !effectiveBehavior.kind) return null;

  switch (effectiveBehavior.kind) {
    case 'due_on_acceptance':
      return invoicedDate;
    case 'due_date_offset': {
      const offset = Number(effectiveBehavior.offset_days);
      if (!Number.isFinite(offset)) return null;
      // IDI-817 D6: count calendar days on the dealer's day as written
      // (YYYY-MM-DD), never through the browser's zone: `new Date(bare)` is
      // UTC midnight, the previous day in a US browser. The key is built
      // from UTC parts so no zone can shift it.
      const m = String(invoicedDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return null;
      const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + offset));
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    }
    case 'milestone_split':
      // PT-26.3 handles the split-schedule. Base helper returns null so
      // callers can layer on split logic without an accidental due-date.
      return null;
    default:
      return null;
  }
}

/**
 * IDI-451: what a payment term means for a due date, from whatever the row
 * carries. In order:
 *   1. the built-in "Due on receipt" (U-22)            -> due on the invoice date
 *   2. a seed `behavior`                                -> as given
 *   3. a migrated `days` (legacy PaymentTerm_Days)      -> N days; 0 only counts as
 *      due on receipt when the NAME says so (COD, cash, receipt). Measured: 114
 *      legacy 0-day terms are "Deposit", "Upon Completion", "Financing", "TBD"...
 *      and have no automatic due date.
 *   4. the name / value ("Net 30", "net_30")            -> 30 days
 *   else null: the user picks the due date.
 */
export const DUE_ON_RECEIPT = { id: 'due_on_receipt', value: 'due_on_receipt', name: 'Due on receipt', behavior: { kind: 'due_on_acceptance' }, system: true };

const RECEIPT_WORDS = /^(cod|c\.o\.d\.?|cash( on delivery)?|due (on|upon) receipt|on receipt|upon receipt)$/i;

export function termBehavior(term, key = '') {
  const name = String(term?.name ?? term?.label ?? key ?? '').trim();
  const id = String(term?.value ?? term?.id ?? key ?? '').trim();
  if (id === DUE_ON_RECEIPT.value || id === 'due_on_acceptance' || /^due (on|upon) receipt$/i.test(name)) {
    return { kind: 'due_on_acceptance' };
  }
  if (term?.behavior?.kind) return term.behavior;
  const days = term?.days ?? term?.dueDays;
  if (days != null && String(days).trim() !== '' && Number.isFinite(Number(days))) {
    const n = Number(days);
    if (n > 0) return { kind: 'due_date_offset', offset_days: n };
    return RECEIPT_WORDS.test(name) ? { kind: 'due_on_acceptance' } : null;
  }
  if (RECEIPT_WORDS.test(name)) return { kind: 'due_on_acceptance' };
  const m = /^net[\s_-]?(\d+)$/i.exec(name) || /^net[\s_-]?(\d+)$/i.exec(id);
  if (m) return { kind: 'due_date_offset', offset_days: Number(m[1]) };
  return null;
}

/**
 * The tenant's payment terms plus the built-in "Due on receipt" (U-22),
 * added once (never duplicated if a tenant already has one by that name).
 */
export function withDueOnReceipt(options) {
  const list = Array.isArray(options) ? options : [];
  const has = list.some((o) => o && (String(o.value ?? o.id) === DUE_ON_RECEIPT.value || /^due (on|upon) receipt$/i.test(String(o.name ?? o.label ?? ''))));
  return has ? list : [...list, { ...DUE_ON_RECEIPT, label: DUE_ON_RECEIPT.name }];
}

/**
 * IDI-451: invoices store their term by NAME (measured: "Net 30", "net_30";
 * no ids). The picker works in ids. These map between the two.
 */
// A term's identity for matching: "Net 30" / "net_30" / "NET-30" all key as
// "net30"; "Due on receipt" / "due_on_receipt" as "dueonreceipt".
const termKey = (v) => String(v ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');

export function termValueFor(stored, options) {
  const raw = String(stored ?? '').trim();
  if (!raw) return '';
  const opts = options || [];
  // 1) exact id/value/name/label
  const lower = raw.toLowerCase();
  let hit = opts.find((o) => o && [o.value, o.id, o.name, o.label].some((v) => v != null && String(v).trim().toLowerCase() === lower));
  // 2) IDI-451: the seed slug vs a migrated name. A quote / the dealer
  //    default stores "net_30"; a migrated tenant's row is {id: "2",
  //    name: "Net 30"}. Without this the picker read "Select…" (J-000030).
  if (!hit) {
    const k = termKey(raw);
    hit = opts.find((o) => o && [o.value, o.id, o.name, o.label].some((v) => v != null && termKey(v) === k));
  }
  return hit ? String(hit.value ?? hit.id) : '';
}

export function termStoredName(value, options) {
  const hit = (options || []).find((o) => o && String(o.value ?? o.id) === String(value));
  return hit ? String(hit.name || hit.label || hit.value || hit.id) : String(value || '');
}

// ─── Invoice Basis ───────────────────────────────────────────────────────────

/**
 * Calculate uninvoiced balance for a job.
 * Invoice basis = accepted_total − Σ(non-void invoices)
 *
 * @param {number} acceptedTotal - Job's accepted total in cents
 * @param {Array<{amount: number, status: string}>} existingInvoices
 * @returns {number} Uninvoiced balance in cents
 */
export function calculateUninvoicedBalance(acceptedTotal, existingInvoices = []) {
  const invoicedTotal = existingInvoices
    .filter((inv) => inv.status !== 'void')
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
  return Math.max(0, acceptedTotal - invoicedTotal);
}

// ─── 50/50 Split ─────────────────────────────────────────────────────────────

/**
 * F1: 50/50 terms create TWO invoice records (Deposit + Completion).
 *
 * @param {number} totalAmount - Total invoice amount in cents
 * @param {string} invoicedDate - ISO date
 * @param {object} baseData - Common invoice fields
 * @returns {Array<object>} Two invoice records
 */
export function create5050Split(totalAmount, invoicedDate, baseData = {}) {
  const depositAmount = Math.round(totalAmount / 2);
  const completionAmount = totalAmount - depositAmount;
  const timestamp = Date.now();

  return [
    {
      ...baseData,
      id: `inv-${timestamp}-deposit`,
      invoiceNumber: null, // Assigned by backend
      amount: depositAmount,
      type: 'deposit',
      label: 'Deposit (50%)',
      terms: '50_50',
      dueDate: invoicedDate, // Deposit due immediately
      linkedInvoiceId: `inv-${timestamp}-completion`,
    },
    {
      ...baseData,
      id: `inv-${timestamp}-completion`,
      invoiceNumber: null,
      amount: completionAmount,
      type: 'completion',
      label: 'Completion (50%)',
      terms: '50_50',
      dueDate: null, // Due on completion
      linkedInvoiceId: `inv-${timestamp}-deposit`,
    },
  ];
}

// ─── Invoice Number Generation ───────────────────────────────────────────────

/**
 * Generate invoice number format: INV-xxxxxx (global sequential).
 * In production, the backend assigns this. This is for display/preview.
 *
 * @param {number} sequence - Sequential number
 * @returns {string} Formatted invoice number
 */
export function formatInvoiceNumber(sequence) {
  return `INV-${String(sequence).padStart(6, '0')}`;
}

// ─── Auto-Draft on Job Completion (REQ-WF-INV-01) ────────────────────────────

/**
 * Create a draft invoice when a job transitions to "Completed".
 * CO bundle-default: approved COs bundled into the invoice by default;
 * user can remove a CO → it stays uninvoiced.
 *
 * PT-23 Shape A: `paymentTermsOptions` threaded through so the internal
 * `deriveDueDate` call resolves against the canonical seed. Currently has
 * no live callers (exported for the future auto-draft-on-completion hook)
 * — callers just need to pass `useSettings.getOptions('payment_terms')`.
 *
 * @param {object} job - The completed job
 * @param {Array} changeOrders - Approved change orders
 * @param {Array} paymentTermsOptions - Canonical `payment_term.v1` options.
 * @returns {object} Draft invoice data ready for persistence
 */
export function createAutoDraftInvoice(job, changeOrders = [], paymentTermsOptions = []) {
  const jobTotal = job.acceptedTotal ?? job.accepted_total ?? job.totalAmount ?? job.total_amount ?? 0;

  // CO bundle-default: approved COs bundled into the invoice
  const approvedCOs = changeOrders.filter(co => co.status === 'approved');
  const coTotal = approvedCOs.reduce((sum, co) => sum + (co.amount || co.total || 0), 0);

  const totalAmount = jobTotal + coTotal;
  const terms = job.paymentTerms || job.payment_terms || 'net_30';
  const invoicedDate = new Date().toISOString().split('T')[0];

  return {
    id: `inv-draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    jobId: job.id,
    customerId: job.customerId || job.customer_id,
    customerName: job.customer_name || job.customerName,
    status: 'draft',
    amount: totalAmount,
    terms,
    invoicedDate,
    dueDate: deriveDueDate(invoicedDate, terms, paymentTermsOptions),
    lineItems: job.lineItems || job.line_items || [],
    changeOrders: approvedCOs.map(co => ({
      id: co.id,
      description: co.reason || co.description,
      amount: co.amount || co.total || 0,
      bundled: true, // Can be un-bundled by user
    })),
    createdAt: new Date().toISOString(),
    autoDrafted: true,
  };
}

// ─── IDI-451: split at any ratio, under any payment term ─────────────────────

/**
 * Split a total into deposit + completion at `depositPercent`.
 *
 * Mirrors the server (cmd/invoices/pricing.go splitInvoiceAmounts): the
 * deposit rounds half away from zero and the COMPLETION absorbs the
 * remainder, so the two parts always sum exactly to the total — no missing
 * or duplicated cent on an odd split like 33.3%.
 *
 * @param {number} totalAmount cents
 * @param {number} depositPercent 0 < p < 100
 * @param {string} invoicedDate ISO date
 * @param {string|null} depositDueDate due date from the selected payment term
 * @returns {Array<object>} [deposit, completion]
 */
export function buildSplitPreview(totalAmount, depositPercent, invoicedDate, depositDueDate = null) {
  const total = Number.isFinite(totalAmount) ? totalAmount : 0;
  const pct = Math.min(Math.max(Number(depositPercent) || 0, 0), 100);
  const depositAmount = Math.round((total * pct) / 100);
  const completionAmount = total - depositAmount;
  const round = (n) => Math.round(n * 10) / 10;
  return [
    {
      type: 'deposit',
      label: `Deposit (${round(pct)}%)`,
      amount: depositAmount,
      dueDate: depositDueDate || invoicedDate,
      milestone: 'deposit',
    },
    {
      type: 'completion',
      label: `Completion (${round(100 - pct)}%)`,
      amount: completionAmount,
      dueDate: null, // due on completion
      milestone: 'completion',
    },
  ];
}

// ─── Send copy (IDI-485; 10-16 wording) ─────────────────────────────────────

/**
 * The stored per-recipient outcomes of the last send (IDI-485):
 * [{email, name, status: sent|redirected|failed, detail, messageId}].
 */
export function invoiceDeliveries(inv) {
  const d = inv && (inv.deliveries || inv.Deliveries);
  const rows = Array.isArray(d) ? d.filter((r) => r && r.email) : [];
  // IDI-580: deliveries are a history (each send's rows carry its sentAt);
  // only the latest send's rows are shown and counted. Rows stored without a
  // sentAt (pre-580) form one earlier send.
  const latest = rows.reduce((m, r) => (r.sentAt && (!m || r.sentAt > m) ? r.sentAt : m), '');
  return rows.filter((r) => (r.sentAt || '') === latest);
}

/** The recipients SES accepted (a dev redirect counts, Adrian 10-20). */
export function acceptedDeliveries(inv) {
  return invoiceDeliveries(inv).filter((r) => r.status === 'sent' || r.status === 'redirected');
}

/** "Invoice I-… sent to a@x.com, b@y.com" (10-16). */
export function invoiceSentToast(number, inv) {
  const label = number ? `Invoice ${number}` : 'Invoice';
  const to = acceptedDeliveries(inv).map((r) => r.email);
  return to.length ? `${label} sent to ${to.join(', ')}` : `${label} sent`;
}

/** IDI-596: the latest send's failed recipient rows (CC rows are never here). */
export function failedDeliveries(inv) {
  return invoiceDeliveries(inv).filter((r) => r.status === 'failed');
}

/** "a@x.com (Address refused); b@y.com (…)" — each failure with its reason. */
function failedList(rows) {
  return rows.map((r) => `${r.email}${r.detail ? ` (${r.detail})` : ''}`).join('; ');
}

/**
 * IDI-596 (D3): some recipients got the invoice and some didn't. The invoice
 * is Sent (Q-14); the warning names who failed and why:
 * "Invoice I-… sent to a@x.com. Failed: b@y.com (Address refused)".
 */
export function invoicePartialToast(number, inv) {
  const label = number ? `Invoice ${number}` : 'Invoice';
  const to = acceptedDeliveries(inv).map((r) => r.email);
  return `${label} sent to ${to.join(', ')}. Failed: ${failedList(failedDeliveries(inv))}`;
}

/** "Deposit I-… sent; completion I-… saved as a draft" (10-16). */
export function splitSentToast(depositNumber, completionNumber) {
  const dep = depositNumber ? `Deposit ${depositNumber}` : 'Deposit';
  const comp = completionNumber ? `completion ${completionNumber}` : 'completion';
  return `${dep} sent; ${comp} saved as a draft`;
}

/** The all-failed message: the invoice is still a draft (Q-14). */
export function invoiceNotSentToast(number, inv) {
  const label = number ? `Invoice ${number}` : 'The invoice';
  const failed = invoiceDeliveries(inv);
  return `${label} wasn't sent to anyone and is still a draft.${failed.length ? ` Failed: ${failedList(failed)}` : ''}`;
}

/**
 * IDI-580: a Send again that reached nobody. The status never changes on a
 * resend, so no "still a draft".
 */
export function resendFailedToast(number, inv) {
  const label = number ? `Invoice ${number}` : 'The invoice';
  const failed = invoiceDeliveries(inv);
  return `${label} wasn't sent to anyone.${failed.length ? ` Failed: ${failedList(failed)}` : ''}`;
}

/**
 * IDI-580 (D1, D4): Send again's composer pre-fill. With a stored lastSend:
 * its subject, message and CC, and the latest send's recipients ticked, then
 * (IDI-597, D1) the customer's other `contacts` unticked. Without one
 * (sent before IDI-485): `defaultSubject` and the customer's contacts
 * (already seeded rows: [{id, email, name, checked}]).
 *
 * @returns {{subject: string, message: string, cc: string[]|null, recipients: Array<{id, email, name, checked}>}}
 */
export function resendPrefill(invoice, contacts = [], defaultSubject = '') {
  const last = invoice && invoice.lastSend;
  if (!last) {
    return {
      subject: defaultSubject,
      message: '',
      cc: null, // 485's own CC seeding (you, then the salesperson)
      recipients: (contacts || []).filter((c) => c && c.email).map((c) => ({ ...c, checked: true })),
    };
  }
  const seen = new Set();
  const recipients = [];
  // IDI-593 (D4): after a send with failures, only the failed are ticked.
  const latest = invoiceDeliveries(invoice);
  const anyFailed = latest.some((r) => r.status === 'failed');
  latest.forEach((r) => {
    const key = String(r.email).trim().toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    recipients.push({ id: `last-${recipients.length}`, email: r.email, name: r.name || '', checked: anyFailed ? r.status === 'failed' : true });
  });
  // IDI-597 (D1): then the customer's other contacts (as the first send
  // lists them), unticked, one row per email.
  (contacts || []).forEach((c) => {
    if (!c || !c.email) return;
    const key = String(c.email).trim().toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    recipients.push({ ...c, checked: false });
  });
  return {
    subject: last.subject || defaultSubject,
    message: last.message || '',
    cc: Array.isArray(last.cc) ? last.cc.filter(Boolean) : [],
    recipients,
  };
}

/**
 * Send Again's toast. Send again (IDI-518) still emails nobody: the server
 * returns the sent invoice unchanged, so this copy must stay honest (IDI-92).
 *
 * @param {string|null|undefined} number  the invoice's I-number
 * @returns {string}
 */
export function honestSentToast(number) {
  const label = number ? `Invoice ${number}` : 'Invoice';
  return `${label} marked as Sent. Email isn't wired yet; nothing was emailed.`;
}

/**
 * "J-000030 · Kitchen remodel" for an invoice header: the job's number and
 * title, whichever exist. Never a uuid: an absent number gives just the title.
 *
 * @param {object|null|undefined} job
 * @param {string|null|undefined} [fallbackNumber]  the invoice's stored jobNumber
 * @returns {string}
 */
export function jobHeaderLabel(job, fallbackNumber) {
  const num =
    (job && (job.projectNumber || job.project_number || job.jobNumber || job.job_number)) ||
    fallbackNumber ||
    '';
  const title = (job && (job.title || job.name)) || '';
  return [String(num).trim(), String(title).trim()].filter(Boolean).join(' · ');
}
